import React, { useState, useRef, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Maximize, RotateCcw } from 'lucide-react';
import { GeneratedImage } from '../types';

interface ImageViewerModalProps {
  image: GeneratedImage;
  isOpen: boolean;
  onClose: () => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({ image, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, image]);

  if (!isOpen) return null;

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.min(Math.max(0.5, scale + delta), 5); // Limits 0.5x to 5x
    setScale(newScale);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.5, 5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.5, 0.5));
  const reset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-sm overflow-hidden" onClick={onClose}>
      
      {/* Controls Bar */}
      <div 
        className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-800/80 border border-slate-700/50 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-4 shadow-xl z-50"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={zoomOut} className="p-2 hover:bg-slate-700 rounded-full text-slate-300 transition-colors" title="缩小">
            <ZoomOut size={20} />
        </button>
        <span className="text-xs font-mono text-slate-400 w-12 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} className="p-2 hover:bg-slate-700 rounded-full text-slate-300 transition-colors" title="放大">
            <ZoomIn size={20} />
        </button>
        <div className="w-px h-4 bg-slate-600"></div>
        <button onClick={reset} className="p-2 hover:bg-slate-700 rounded-full text-slate-300 transition-colors" title="重置">
            <RotateCcw size={18} />
        </button>
      </div>

      <button 
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-slate-800/50 hover:bg-red-500/20 hover:text-red-400 rounded-full text-slate-400 transition-all z-50"
      >
        <X size={24} />
      </button>
      
      {/* Image Container */}
      <div 
        ref={containerRef}
        className="w-full h-full flex items-center justify-center cursor-move"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={e => e.stopPropagation()}
      >
        <img 
            src={image.url} 
            alt="Full view" 
            className="max-w-none transition-transform duration-75 ease-out select-none"
            style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                maxHeight: '90vh',
                maxWidth: '90vw'
            }}
            draggable={false}
        />
      </div>

      <div className="absolute bottom-6 left-6 text-white text-sm bg-black/50 px-3 py-1 rounded backdrop-blur-sm pointer-events-none select-none">
         提示词: <span className="text-slate-300 font-light">{image.promptUsed.slice(0, 60)}{image.promptUsed.length > 60 ? '...' : ''}</span>
      </div>
    </div>
  );
};

export default ImageViewerModal;